import { CalendarBlank, ClockCountdown, DotOutline, VideoCamera } from '@liveauctioneers/hammer-ui-core/icons';
import { Link } from '@liveauctioneers/hammer-ui-core/link';
import { BodySecondary } from '@liveauctioneers/hammer-ui-core/text';
import { Tooltip } from '@liveauctioneers/hammer-ui-core/tooltip';
import { cn } from '@liveauctioneers/hammer-ui-theme/cn';
import { ReactNode, useEffect, useState } from 'react';

import { AddToCalendar } from '@/components/AddToCalendar/AddToCalendar';
import { useAppSelector } from '@/redux/hooks';
import {
    getCatalogDescription,
    getCatalogEndTime,
    getCatalogHasVideo,
    getCatalogIsCatalogOnly,
    getCatalogIsDone,
    getCatalogIsTimedPlus,
    getCatalogIsTimedPlusBiddingOpen,
    getCatalogIsTimedPlusClosing,
    getCatalogSellerName,
    getCatalogStartTime,
    getCatalogStatus,
    getCatalogTimedPlusBiddingEndsFromTs,
    getCatalogTitle,
} from '@/redux/modules/catalog/catalogs/catalog.selectors';
import { useTrackAddToCalendarPressedAnalytics } from '@/utils/amplitude/analytics/registration/trackAddToCalendarPressed.analytics';
import { createCalendarEvent } from '@/utils/calendarEvent';
import { longFormatDate } from '@/utils/dates';
import { useAppFormatMessage } from '@/utils/intl/useAppFormatMessage';
import { shouldShowLive } from '@/utils/live';
import { getHowTimedAuctionsWorkUrl } from '@/utils/urls';

type Props = {
    catalogId: number;
    className?: string;
    forceIsLive?: boolean;
    forceOneDate?: boolean;
    hideBorders?: boolean;
    hideIcon?: boolean;
    hideStartsOnLabel?: boolean;
    showAddToCalendar?: boolean;
    showClosedLabel?: boolean;
    stackTimedPlusLink?: boolean;
    timedPlusLink?: ReactNode;
    variant?: 'default' | 'detailsZone';
};

export const CatalogDate = ({
    catalogId,
    className,
    forceIsLive,
    forceOneDate,
    hideBorders,
    hideIcon,
    hideStartsOnLabel,
    showAddToCalendar,
    showClosedLabel,
    stackTimedPlusLink,
    timedPlusLink,
    variant = 'default',
}: Props) => {
    const trackAddToCalendarPressed = useTrackAddToCalendarPressedAnalytics();
    const catalogTitle = useAppSelector((state) => getCatalogTitle(state, catalogId));
    const catalogDescription = useAppSelector((state) => getCatalogDescription(state, catalogId));
    const sellerName = useAppSelector((state) => getCatalogSellerName(state, catalogId));
    const isCatalogDone = useAppSelector((state) => getCatalogIsDone(state, catalogId));
    const isCatalogTimedPlus = useAppSelector((state) => getCatalogIsTimedPlus(state, catalogId));
    const biddingEndsFrom =
        useAppSelector((state) => getCatalogTimedPlusBiddingEndsFromTs(state, catalogId)) || Date.now() / 1000;
    const biddingOpens = useAppSelector((state) => getCatalogStartTime(state, catalogId)) || Date.now() / 1000;
    const catalogStatus = useAppSelector((state) => getCatalogStatus(state, catalogId));
    const hasVideo = useAppSelector((state) => getCatalogHasVideo(state, catalogId));
    const isCatalogOnly = useAppSelector((state) => getCatalogIsCatalogOnly(state, catalogId));
    const saleEndTs = useAppSelector((state) => getCatalogEndTime(state, catalogId));
    const timedPlusBiddingOpen = useAppSelector((state) => getCatalogIsTimedPlusBiddingOpen(state, catalogId));
    const timedPlusClosing = useAppSelector((state) => getCatalogIsTimedPlusClosing(state, catalogId));
    const isLive = forceIsLive || shouldShowLive(catalogStatus, isCatalogOnly, biddingOpens);
    const showTwoDates = !forceOneDate && !timedPlusBiddingOpen;
    const formatMessage = useAppFormatMessage();
    const isDetailsZone = variant === 'detailsZone';
    const dateColor = isDetailsZone ? 'primary' : 'secondary';
    const shouldStackTimedPlusLink = Boolean(timedPlusLink && stackTimedPlusLink && !timedPlusClosing);

    const [biddingOpensFromDate, setBiddingOpensFromDate] = useState('');
    const [saleEndDate, setSaleEndDate] = useState('');
    const [biddingEndsFromDate, setBiddingEndsFromDate] = useState('');

    useEffect(() => {
        setBiddingOpensFromDate(longFormatDate(biddingOpens));
        setSaleEndDate(longFormatDate(saleEndTs));
        setBiddingEndsFromDate(longFormatDate(biddingEndsFrom));
    }, [biddingEndsFrom, biddingOpens, saleEndTs]);

    const calendarEventObject = createCalendarEvent({
        catalogId,
        description: catalogDescription,
        saleStartTs: biddingOpens,
        sellerName,
        title: catalogTitle ?? '',
    });

    const handleCalendarTypeClick = (calendarType: string) => {
        trackAddToCalendarPressed({ calendarType });
    };

    const timedPlusDateLabel = timedPlusClosing ? (
        <BodySecondary
            className={cn('block', isDetailsZone && 'font-semibold')}
            color={dateColor}
        >
            {formatMessage(['catalogPage', 'catalogDate', isDetailsZone ? 'endingNowExperiment' : 'endingNow'])}
        </BodySecondary>
    ) : (
        <BodySecondary
            className="block"
            color={dateColor}
        >
            {formatMessage(['catalogPage', 'catalogDate', 'endsFrom'], {
                values: { date: biddingEndsFromDate },
            })}
        </BodySecondary>
    );

    const timedPlusAboutLink = timedPlusLink ? (
        shouldStackTimedPlusLink ? (
            <div
                className="flex flex-col items-start gap-2xs"
                data-testid="timed-plus-about-link-stack"
            >
                {timedPlusDateLabel}
                {timedPlusLink}
            </div>
        ) : (
            <div className="flex flex-wrap items-center gap-xs">
                {timedPlusDateLabel}
                <DotOutline
                    color="primary"
                    size="xs"
                    style="fill"
                />
                {timedPlusLink}
            </div>
        )
    ) : (
        timedPlusDateLabel
    );

    const timedPlusIcon = !hideIcon && (
        <div
            className={cn(
                'flex flex-row items-center justify-center [&>div]:h-5',
                !isDetailsZone && 'mr-xs xsMax:mr-2xs'
            )}
        >
            <ClockCountdown
                color="primary"
                data-testid="timed-plus-icon"
                id="catalog-date-timed-plus-icon-trigger"
                size="sm"
            />
            {!timedPlusLink && (
                <Tooltip anchorSelect="#catalog-date-timed-plus-icon-trigger">
                    {formatMessage(['catalogPage', 'catalogDate', 'timedPlusMessage'], {
                        values: {
                            link: (
                                <Link
                                    key="catalog-date-timed-plus-message-link"
                                    className={isDetailsZone ? 'text-link-secondary' : undefined}
                                    href={getHowTimedAuctionsWorkUrl()}
                                    rel="noopener"
                                    target="_blank"
                                >
                                    {formatMessage(['catalogPage', 'catalogDate', 'learnMore'])}
                                </Link>
                            ),
                        },
                    })}
                </Tooltip>
            )}
        </div>
    );

    const liveTimedCalendarIcon = isDetailsZone && !hideIcon && (
        <div className="flex flex-row items-center justify-center [&>div]:h-5">
            {hasVideo && !isCatalogTimedPlus && !isCatalogDone ? (
                <VideoCamera
                    color="primary"
                    data-testid="live-timed-video-icon"
                    size="sm"
                    style="fill"
                />
            ) : (
                <CalendarBlank
                    color="primary"
                    data-testid="live-timed-calendar-icon"
                    size="sm"
                />
            )}
        </div>
    );

    return (
        <div
            className={cn(
                'flex flex-row justify-start border-r border-border-secondary pr-md xsMax:border-r-0',
                'items-start',
                'xsMax:items-start',
                !isDetailsZone && 'xsMax:mb-xs',
                hideBorders && 'border-none',
                className
            )}
            data-testid="catalog-date"
        >
            {isCatalogDone ? (
                <>
                    {isCatalogTimedPlus ? timedPlusIcon : liveTimedCalendarIcon}
                    <div
                        className={cn('flex flex-wrap items-center', isDetailsZone && 'ml-xs gap-xs')}
                        data-testid="catalog-date-status-row"
                    >
                        <BodySecondary
                            className={cn('uppercase', showClosedLabel && 'font-bold')}
                            color={dateColor}
                        >
                            {formatMessage(['catalogPage', 'catalogDate', showClosedLabel ? 'closed' : 'done'])}
                        </BodySecondary>
                        {Boolean(saleEndDate) &&
                            (isDetailsZone ? (
                                <>
                                    <DotOutline
                                        aria-hidden
                                        color="primary"
                                        size="xs"
                                        style="fill"
                                    />
                                    <BodySecondary
                                        color={dateColor}
                                        data-testid="end-time"
                                    >
                                        {saleEndDate}
                                    </BodySecondary>
                                </>
                            ) : (
                                <BodySecondary
                                    className="ml-sm border-l border-border-secondary pl-sm xsMax:ml-xs xsMax:pl-xs"
                                    color="secondary"
                                    data-testid="end-time"
                                >
                                    {saleEndDate}
                                </BodySecondary>
                            ))}
                    </div>
                </>
            ) : isCatalogTimedPlus ? (
                <>
                    {timedPlusIcon}
                    <div
                        className={cn(
                            'flex flex-col items-start justify-center',
                            isDetailsZone
                                ? 'ml-xs'
                                : 'xsMax:ml-2xs xsMax:border-l xsMax:border-border-secondary xsMax:pl-xs'
                        )}
                    >
                        {showTwoDates && (
                            <BodySecondary
                                className="mb-2xs block"
                                color={dateColor}
                            >
                                {formatMessage(['catalogPage', 'catalogDate', 'startsOn'], {
                                    values: { date: biddingOpensFromDate },
                                })}
                            </BodySecondary>
                        )}
                        {timedPlusAboutLink}
                    </div>
                </>
            ) : isLive ? (
                <>
                    {liveTimedCalendarIcon}
                    <BodySecondary
                        className={cn('block', isDetailsZone && 'ml-xs')}
                        color={dateColor}
                        data-testid="timed-or-live-date"
                    >
                        {formatMessage(['catalogPage', 'catalogDate', 'started'], {
                            values: { date: biddingOpensFromDate },
                        })}
                    </BodySecondary>
                </>
            ) : showAddToCalendar ? (
                <>
                    {liveTimedCalendarIcon}
                    <div className={cn('flex flex-wrap items-center gap-xs', isDetailsZone && 'ml-xs')}>
                        <BodySecondary
                            color={dateColor}
                            data-testid="timed-or-live-date"
                        >
                            {hideStartsOnLabel
                                ? biddingOpensFromDate
                                : formatMessage(['catalogPage', 'catalogDate', 'startsOn'], {
                                      values: { date: biddingOpensFromDate },
                                  })}
                        </BodySecondary>
                        <DotOutline
                            aria-hidden
                            color="primary"
                            size="xs"
                            style="fill"
                        />
                        <AddToCalendar
                            buttonLabel={String(formatMessage(['catalogPage', 'catalogDate', 'addToCalendar']))}
                            event={calendarEventObject}
                            onClick={handleCalendarTypeClick}
                            variant="inline"
                        />
                    </div>
                </>
            ) : (
                <BodySecondary
                    className="block"
                    color="secondary"
                    data-testid="timed-or-live-date"
                >
                    {formatMessage(['catalogPage', 'catalogDate', 'startsOn'], {
                        values: { date: biddingOpensFromDate },
                    })}
                </BodySecondary>
            )}
        </div>
    );
};
